import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  Launch as LaunchIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import paybosService from '../services/paybosService';
import { PaybosPayment } from '../types/paybos';
import QrisQrDisplay from './QrisQrDisplay';
import PaybosFeeBreakdown from './PaybosFeeBreakdown';

function calcServiceFee(
  billAmount: number,
  fixed = 0,
  percent = 0
) {
  const percentPart = Math.round((billAmount * percent) / 100);
  return Math.max(0, Math.round(fixed + percentPart));
}

interface PaybosPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  billNumber: string;
  totalAmount: number;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  onPaymentCreated?: (payment: PaybosPayment) => void;
}

const PaybosPaymentDialog: React.FC<PaybosPaymentDialogProps> = ({
  open,
  onClose,
  billId,
  billNumber,
  totalAmount,
  customerName = '',
  customerEmail = '',
  customerPhone = '',
  onPaymentCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPayment, setCreatedPayment] = useState<PaybosPayment | null>(null);
  const [step, setStep] = useState<'form' | 'result'>('form');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
  });
  const [feeFixed, setFeeFixed] = useState(0);
  const [feePercent, setFeePercent] = useState(0);

  useEffect(() => {
    if (open) {
      setFormData({
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
      });
      setStep('form');
      setError(null);
      setCreatedPayment(null);
      paybosService.config.getConfig().then((res) => {
        if (res.success && res.data) {
          setFeeFixed(Number(res.data.service_fee_fixed || 0));
          setFeePercent(Number(res.data.service_fee_percent || 0));
        }
      }).catch(() => {
        setFeeFixed(0);
        setFeePercent(0);
      });
    }
  }, [open, customerName, customerEmail, customerPhone]);

  const serviceFee = calcServiceFee(totalAmount, feeFixed, feePercent);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);

  const handleCreate = async () => {
    if (!formData.customer_name || !formData.customer_email) {
      setError('Nama dan email pelanggan wajib diisi');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await paybosService.payment.createPayment({
        bill_id: billId,
        customer_data: {
          name: formData.customer_name,
          email: formData.customer_email,
          phone: formData.customer_phone,
        },
      });
      if (response.success) {
        setCreatedPayment(response.data);
        setStep('result');
        onPaymentCreated?.(response.data);
      } else {
        setError(response.message || 'Gagal membuat pembayaran PayBOS');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal membuat pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" />
            Pembayaran QRIS PayBOS
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Tagihan: <strong>{billNumber}</strong>
        </Typography>
        <Box sx={{ mb: 2 }}>
          <PaybosFeeBreakdown
            billAmount={totalAmount}
            serviceFee={serviceFee}
            formatCurrency={formatCurrency}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'form' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nama Pelanggan"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="No. Telepon"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              fullWidth
            />
          </Stack>
        )}

        {step === 'result' && createdPayment && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">Pembayaran QRIS PayBOS berhasil dibuat</Alert>
            <Chip label={`Status: ${createdPayment.status}`} color="warning" />
            <Typography variant="body2">Referensi: {createdPayment.reference_id}</Typography>
            <PaybosFeeBreakdown
              billAmount={Number(createdPayment.bill_amount || totalAmount)}
              serviceFee={Number(createdPayment.service_fee || 0)}
              totalAmount={Number(createdPayment.amount)}
              formatCurrency={formatCurrency}
            />
            {createdPayment.qr_string && (
              <QrisQrDisplay
                qrString={createdPayment.qr_string}
                amount={Number(createdPayment.amount)}
                downloadFileName={`qris-${billNumber}.png`}
              />
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<LaunchIcon />}
                href={createdPayment.payment_url}
                target="_blank"
              >
                Buka di PayBOS
              </Button>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(`${window.location.origin}/payment/paybos/bill/${billId}`)}
              >
                Copy Link Pelanggan
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'form' && (
          <Button onClick={handleCreate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Buat Pembayaran QRIS'}
          </Button>
        )}
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaybosPaymentDialog;
